import * as React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Trash2, X } from 'lucide-react';
import { Button, Typography } from '@/components/common';
import { useFocusTrap } from './hooks/useFocusTrap';

export interface SignaturePadModalProps {
	isOpen: boolean;
	onClose: () => void;
	onApply: (dataUrl: string) => void;
}

const PAD_HEIGHT = 190;

export const SignaturePadModal: React.FC<SignaturePadModalProps> = ({
	isOpen,
	onClose,
	onApply,
}) => {
	const padRef = React.useRef<SignatureCanvas | null>(null);
	const dialogRef = React.useRef<HTMLDivElement>(null);
	const padContainerRef = React.useRef<HTMLDivElement>(null);
	const [padWidth, setPadWidth] = React.useState(0);
	const [isEmpty, setIsEmpty] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);

	useFocusTrap(isOpen, dialogRef, onClose);

	React.useEffect(() => {
		if (isOpen) {
			setError(null);
			setIsEmpty(true);
		}
	}, [isOpen]);

	React.useLayoutEffect(() => {
		const container = padContainerRef.current;
		if (!isOpen || !container) return;
		const updateWidth = () => setPadWidth(container.clientWidth);
		updateWidth();
		const observer = new ResizeObserver(updateWidth);
		observer.observe(container);
		return () => observer.disconnect();
	}, [isOpen]);

	React.useEffect(() => {
		setIsEmpty(true);
	}, [padWidth]);

	if (!isOpen) return null;

	const handleClear = () => {
		setError(null);
		padRef.current?.clear();
		setIsEmpty(true);
	};

	const handleApply = () => {
		try {
			if (!padRef.current || padRef.current.isEmpty()) {
				setError('Please draw your signature before confirming it.');
				return;
			}
			onApply(padRef.current.toDataURL('image/png'));
		} catch {
			setError('The signature pad could not be used. Please try again.');
		}
	};

	return (
		<div className="absolute inset-0 z-40 flex items-center justify-center bg-dark-100/40 p-4">
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="signature-pad-title"
				aria-describedby="signature-pad-description"
				tabIndex={-1}
				className="flex w-full max-w-[760px] flex-col overflow-hidden rounded-2xl bg-white outline-none shadow-chatBox"
			>
				<div className="flex items-start justify-between gap-4 border-b border-[#0D061614] px-4 py-4 md:px-6">
					<div className="flex flex-col gap-0.5">
						<Typography
							type="semibold-16"
							className="text-left"
							id="signature-pad-title"
						>
							Draw your signature
						</Typography>
						<Typography
							type="regular-12"
							id="signature-pad-description"
							className="text-dark-100/70"
						>
							Use your mouse or finger to sign below
						</Typography>
					</div>
					<button
						type="button"
						aria-label="Close"
						onClick={onClose}
						className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-5 text-dark-100 hover:bg-[#0D06160F]"
					>
						<X className="size-4" />
					</button>
				</div>

				<div className="flex flex-col gap-4 p-4 md:px-6 md:pb-5">
					<div
						ref={padContainerRef}
						className="relative overflow-hidden rounded-2xl border border-[#0D06161F] bg-[#FAFAFA]"
						style={{ height: PAD_HEIGHT }}
					>
						{isEmpty && (
							<span
								aria-hidden="true"
								className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-opacity-black"
							>
								Sign here...
							</span>
						)}
						{padWidth > 0 && (
							<SignatureCanvas
								ref={padRef}
								penColor="#0D0616"
								onBegin={() => {
									setError(null);
									setIsEmpty(false);
								}}
								canvasProps={{
									className: 'relative block touch-none cursor-crosshair',
									width: padWidth,
									height: PAD_HEIGHT,
									'aria-label': 'Signature drawing area',
								}}
							/>
						)}
					</div>

					{error && (
						<Typography type="regular-14" className="text-main-red">
							{error}
						</Typography>
					)}

					<div className="flex items-center gap-4 md:gap-9">
						<button
							type="button"
							onClick={handleClear}
							disabled={isEmpty}
							className="flex h-[42px] w-[140px] shrink-0 items-center justify-center gap-2 rounded-full border border-[#0D061614] bg-white text-sm font-semibold text-dark-100 hover:bg-gray-5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white md:w-[176px]"
						>
							<Trash2 className="size-4" />
							Clear
						</button>
						<Button
							type="button"
							size="full"
							decoration="primary"
							label="Confirm Signature"
							className="!h-[42px]"
							disabled={isEmpty}
							onClick={handleApply}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SignaturePadModal;
